<template>
    <div
        :class="[
            'input-wrapper',
            'input-wrapper--checkbox',
            'input-wrapper--' + name,
            'input-wrapper--' + inputSize,
            isHighlighted ? 'highlighted' : '',
        ]"
    >
        <label :class="[hasError ? 'has-error' : '']">
            <input
                v-model="model"
                type="checkbox"
                :name="name"
                :disabled="disabled"
                class="visually-hidden"
                @change="update"
                @focus="setHighlighted(true)"
                @blur="setHighlighted(false)"
            />
            <div
                :class="[
                    'input__label',
                    'input__label--' + name,
                    'input__label--checkbox',
                ]"
            >
                <slot />
            </div>
        </label>
        <Error v-if="handlesError && !model" :name="name" />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, toRefs } from 'vue';
import { useStore } from 'vuex';
import Error from '@/components/basic/Error.vue';
import { DELETE_ERROR } from '@/constants/mutation-types';

export enum CheckboxSize {
    Large = 'large',
    Medium = 'medium',
}

export default defineComponent({
    name: 'Checkbox',
    components: {
        Error,
    },
    props: {
        name: {
            type: String,
            default: '',
        },
        modelValue: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        handlesError: {
            type: Boolean,
            default: true,
        },
        isValidated: {
            type: Boolean,
            default: true,
        },
        inputSize: {
            type: String as () => CheckboxSize,
            default: CheckboxSize.Large,
        },
        noAutoValidation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const store = useStore();
        const { name, isValidated } = toRefs(props);
        const model = ref(props.modelValue);
        const hasError = ref(false);
        const isHighlighted = ref(false);

        const isErrorsChanged = computed(() => store.state.isErrorsChanged);
        const formDataStore = computed(() => store.state.formData);

        const update = () => {
            setHighlighted(false);
            store.commit(DELETE_ERROR, name.value);
            emit('update:modelValue', model.value);

            if (isValidated.value) {
                if (props.noAutoValidation) {
                    return;
                }
                store.dispatch('validate', {
                    name: name.value,
                    value: model.value,
                });
            }
        };

        const loadData = () => {
            const formData = formDataStore.value as Record<string, unknown>;
            if (Object.prototype.hasOwnProperty.call(formData, name.value)) {
                model.value = Boolean(formData[name.value]);
            }
        };

        const setHighlighted = (value: boolean) => {
            isHighlighted.value = value;
        };

        watch(isErrorsChanged, () => {
            hasError.value = store.state.errors.get(name.value);
        });

        watch(
            () => props.modelValue,
            (newVal) => {
                model.value = newVal;
            }
        );
        watch(model, (newModel) => {
            store.commit(DELETE_ERROR, name.value);
            emit('update:modelValue', newModel);
        });

        onMounted(() => {
            loadData();
        });

        return {
            model,
            hasError,
            isHighlighted,
            update,
            loadData,
            setHighlighted,
        };
    },
});
</script>
